import { makeStyles, tokens } from '@fluentui/react-components';
import React from "react";
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';

import { useCommand, useSelector } from '@tdp';
import { AppDetailsHeader } from '@tdp/common/components/AppDetailsHeader';
import { makeDocsLink } from "@tdp/common/constants";

import { ValidationDetails } from "../components/ValidationDetails";
import { ValidationInfo } from "../components/ValidationInfo";
import { ValidationPublish } from "../components/ValidationPublish";
import { ValidationStatus } from "../components/ValidationStatus";
import { ValidationWarning } from "../components/ValidationWarning";
import { appValidationSlice } from "../redux/slice";


const useStyles = makeStyles({
  pageContainer: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorBrandBackgroundInverted,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    boxSizing: 'border-box',
    height: '100%',
    width: '100%',
    overflowY: 'auto',
    padding: "18px",
    gap: '1rem',
    maxWidth: "1200px"
  },
  cardRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: "wrap",
    gap: '1rem',
  }
})

export const AppValidationDetailPage: React.FC = () => {
  const classes = useStyles();
  const { validationId } = useParams();
  const navigate = useNavigate()

  // Selectors
  const currentValidation = useSelector(s => appValidationSlice.selectors.currentValidation(s));
  const newValidationId = useSelector(s => appValidationSlice.selectors.newValidationId(s))

  // Commands
  const resetCurrentValidation = useCommand(s => s.appValidationCommands.resetCurrentValidation);
  const getValidationDetails = useCommand(s => s.appValidationCommands.getValidationDetails);
  const resetNewValidationId = useCommand(s => s.appValidationCommands.resetNewValidationId);

  React.useEffect(() => {
    if (!validationId) { return; }
    if (currentValidation?.appValidationId !== validationId) { resetCurrentValidation(); }
    if (!currentValidation) { getValidationDetails({ validationId }); }
    return () => { resetNewValidationId(); }
  }, [validationId, currentValidation, newValidationId]);

  const onBackNavigation = () => {
    navigate(-1);
  }
  return (
    <div className={classes.pageContainer}>
      <AppDetailsHeader
        onBackNavigation={() => { onBackNavigation(); }}
        showBackNavigation={true}
        title={<FormattedMessage
          defaultMessage="App Validation"
          description={'Title for App Validation Section'}
        />}
        description={<FormattedMessage
          defaultMessage="Prepare your app for its launch and ensure it meets Microsoft's test cases."
          description={'Description for App Validation Section'}
        />}
        link={<FormattedMessage
          defaultMessage="Learn more about app validation"
          description={'Link to learn more about App Validation'}
        />}
        href={makeDocsLink("microsoftteams/platform/concepts/build-and-test/manage-your-apps-in-developer-portal#publish")}
      />
      <section className={classes.cardRow}>
        <ValidationInfo currentValidation={currentValidation} />
        <ValidationStatus currentValidation={currentValidation} />
        <ValidationPublish currentValidation={currentValidation} />
      </section>
      <section>
        <ValidationWarning currentValidation={currentValidation} />
      </section>
      <ValidationDetails currentValidation={currentValidation} />
    </div>
  );
}
